import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlatform } from "../context/PlatformContext";
import { LogOut, Building2, Users as UsersIcon, FolderKanban, Settings, UserCog } from "lucide-react";
import { motion } from "framer-motion";
import AddFirmDialog from "./superadmin/AddFirmDialog";
import FirmsTab from "./superadmin/FirmsTab";
import AppUsersTab from "./superadmin/AppUsersTab";
import PlatformStaffTab from "./superadmin/PlatformStaffTab";
import SubscriptionTiersTab from "./superadmin/SubscriptionTiersTab";
import MilestoneTemplatesEditor from "../components/MilestoneTemplatesEditor";

const TABS = [
  { key: "firms", label: "ID firms" },
  { key: "users", label: "App users" },
  { key: "staff", label: "Platform staff" },
  { key: "milestones", label: "Milestone library" },
  { key: "subscriptions", label: "Subscription tiers", superAdminOnly: true },
];

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const { settings } = usePlatform();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [platformMilestones, setPlatformMilestones] = useState(null);
  const [tab, setTab] = useState("firms");
  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    const calls = [
      api.get("/superadmin/stats"),
      api.get("/superadmin/workspaces"),
      api.get("/superadmin/users"),
    ];
    if (isSuperAdmin) {
      calls.push(api.get("/superadmin/platform-milestones"));
    }
    const results = await Promise.all(calls);
    setStats(results[0].data);
    setWorkspaces(results[1].data.workspaces || []);
    setUsers(results[2].data.users || []);
    if (results[3]) setPlatformMilestones(results[3].data.templates || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  const metrics = [
    { key: "workspaces", label: "ID firms", value: stats?.workspaces ?? "—", icon: Building2, tint: "#FFEDD5", fg: "#C2410C" },
    { key: "users", label: "App users", value: users.length, icon: UsersIcon, tint: "#DBEAFE", fg: "#1E40AF" },
    { key: "projects", label: "Projects", value: stats?.projects ?? "—", icon: FolderKanban, tint: "#CCFBF1", fg: "#0F766E" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#EBE5D9]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="w-9 h-9 rounded-2xl object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-2xl bg-[#2C2A29] flex items-center justify-center text-white font-display font-bold">R</div>
            )}
            <div>
              <div className="font-display text-xl font-semibold text-[#2C2A29] leading-tight">{settings.company_name}</div>
              <div className="text-xs text-[#7A7571]">Platform Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isSuperAdmin && (
              <Link to="/admin/settings" data-testid="admin-settings-link" className="btn-ghost inline-flex items-center gap-2">
                <Settings size={16} /> <span className="hidden sm:inline">Settings</span>
              </Link>
            )}
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-[#2C2A29]">{user?.name}</div>
              <div className="text-xs text-[#7A7571] capitalize">{(user?.role || "").replace("_", " ")}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="btn-ghost !p-2"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Platform</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2C2A29] mt-2">
              Every firm, <span className="italic text-[#E07A5F]">every project.</span>
            </h1>
          </div>
          {isSuperAdmin && tab === "firms" && <AddFirmDialog onDone={load} />}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {metrics.map((m, i) => (
            <motion.div
              key={m.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="card-soft"
              data-testid={`sa-metric-${m.key}`}
            >
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5" style={{ background: m.tint, color: m.fg }}>
                <m.icon size={20} />
              </div>
              <div className="text-3xl font-display font-bold text-[#2C2A29]">{m.value}</div>
              <div className="text-sm text-[#7A7571] mt-1">{m.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="flex gap-8 border-b border-[#EBE5D9] mb-8 overflow-x-auto hide-scrollbar">
          {TABS.filter((tb) => !tb.superAdminOnly || isSuperAdmin).map((t) => (
            <button
              key={t.key} onClick={() => setTab(t.key)}
              data-testid={`sa-tab-${t.key}`}
              className={`tab-btn whitespace-nowrap ${tab === t.key ? "tab-btn-active" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "firms" && <FirmsTab workspaces={workspaces} onReload={load} />}
        {tab === "users" && <AppUsersTab users={users} onReload={load} />}
        {tab === "staff" && <PlatformStaffTab isSuperAdmin={isSuperAdmin} />}
        {tab === "milestones" && isSuperAdmin && platformMilestones !== null && (
          <MilestoneTemplatesEditor
            title="Platform default milestones"
            subtitle="These are seeded into every new ID firm's library when they sign up. Editing this list does NOT retroactively change firms that already exist."
            initial={platformMilestones}
            saveEndpoint="/superadmin/platform-milestones"
            onSaved={(t) => setPlatformMilestones(t)}
          />
        )}
        {tab === "subscriptions" && isSuperAdmin && <SubscriptionTiersTab />}
      </main>
    </div>
  );
}
